import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from 'react-bootstrap/Modal';
import api from './api/axiosInstance';
import base_url from './api/Config';
import TopBar from './component/TopBar';

const ERR_STYLE = {
  fontSize: 12,
  color: '#e53935',
  marginTop: 4,
  marginBottom: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 4,
};

const FieldError = ({ msg }) =>
  msg ? (
    <p style={ERR_STYLE}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 15, height: 15, borderRadius: '50%',
        background: '#e53935', color: '#fff', fontSize: 9, fontWeight: 700, flexShrink: 0,
      }}>!</span>
      {msg}
    </p>
  ) : null;

const invalidInput = { borderColor: '#e53935', background: '#fff8f8' };

const Register = () => {
  const [form, setForm] = useState({
    name: '', mobileNo: '', email: '', dob: '', gender: '',
    maritalStatus: '', spouseName: '', panNumber: '', aadhaarNumber: '',
    fatherName: '', pinCode: '', loanAmount: '', loanType: '', loanGroup: '',
    updatesConsent: false, termsConsent: false,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [applicationId, setApplicationId] = useState('');
  const [customerId, setCustomerId] = useState('');

  const navigate = useNavigate();

  const set = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, [field]: val }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const setNum = (field) => (e) => {
    const val = e.target.value.replace(/\D/g, '');
    setForm(f => ({ ...f, [field]: val }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validateEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required.';
    if (!form.email.trim()) e.email = 'Email address is required.';
    else if (!validateEmail(form.email)) e.email = 'Enter a valid email (e.g. name@example.com).';
    if (!form.mobileNo.trim()) e.mobileNo = 'Mobile number is required.';
    else if (!/^[6-9]/.test(form.mobileNo)) e.mobileNo = 'Mobile number must start with 6, 7, 8, or 9.';
    else if (form.mobileNo.length !== 10) e.mobileNo = 'Mobile number must be exactly 10 digits.';
    if (!form.dob) e.dob = 'Date of birth is required.';
    else {
      const today = new Date();
      const birth = new Date(form.dob);
      let age = today.getFullYear() - birth.getFullYear();
      const m = today.getMonth() - birth.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
      if (age < 18) e.dob = 'Customer must be at least 18 years old.';
      else if (age > 55) e.dob = 'Customer must not be older than 55 years.';
    }
    if (!form.gender) e.gender = 'Please select gender.';
    if (!form.maritalStatus) e.maritalStatus = 'Please select marital status.';
    if (form.maritalStatus === '2' && !form.spouseName.trim()) e.spouseName = 'Spouse name is required for married applicants.';
    if (!form.fatherName.trim()) e.fatherName = "Father's name is required.";
    if (!form.panNumber.trim()) e.panNumber = 'PAN number is required.';
    else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(form.panNumber.toUpperCase()))
      e.panNumber = 'Invalid PAN format. Example: ABCDE1234F';
    if (!form.aadhaarNumber.trim()) e.aadhaarNumber = 'Aadhaar number is required.';
    else if (!/^\d+$/.test(form.aadhaarNumber)) e.aadhaarNumber = 'Aadhaar number must contain only digits.';
    else if (form.aadhaarNumber.length !== 12) e.aadhaarNumber = 'Aadhaar number must be exactly 12 digits.';
    if (!form.pinCode.trim()) e.pinCode = 'Pin code is required.';
    else if (form.pinCode.length !== 6) e.pinCode = 'Pin code must be exactly 6 digits.';
    if (!form.loanAmount.trim()) e.loanAmount = 'Loan amount is required.';
    if (!form.loanType) e.loanType = 'Please select a loan type.';
    if (!form.loanGroup) e.loanGroup = 'Please select a loan group.';
    if (!form.updatesConsent) e.updatesConsent = 'Please agree to receive updates to continue.';
    if (!form.termsConsent) e.termsConsent = 'You must accept the Terms & Conditions to proceed.';
    return e;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      const firstKey = Object.keys(errs)[0];
      document.getElementById(`field-${firstKey}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setLoading(true);
    try {
      const fintechId = localStorage.getItem('fintechId') || '';

      const response = await api.post(`${base_url}/api/customer/addCustomerData`, {
        partnerId: fintechId,
        borrowerName: form.name,
        mobileNumber: form.mobileNo,
        email: form.email,
        dob: form.dob,
        gender: form.gender,
        maritalStatus: form.maritalStatus,
        panNumber: form.panNumber.toUpperCase(),
        aadhaarNumber: form.aadhaarNumber,
        fatherName: form.fatherName,
        spouseName: form.maritalStatus === '2' ? form.spouseName : null,
        pinCode: form.pinCode,
        loanAmount: form.loanAmount,
        loanType: form.loanType,
        loanGroup: form.loanGroup,
        updatesConsent: form.updatesConsent,
        termsConsent: form.termsConsent,
      });

      const cid = response.data.customerId || response.data.id || '';
      setCustomerId(cid);
      setApplicationId(response.data.applicationId || cid);
      setShowSuccessModal(true);
    } catch (err) {
      const data = err.response?.data;
      let msg = 'Registration failed. Please try again.';
      if (Array.isArray(data?.errors)) msg = data.errors.join('\n');
      else if (data?.message) msg = data.message;
      setApiError(msg);
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const inp = (field) => errors[field] ? invalidInput : {};

  return (
    <div className="app-page">
      <TopBar title="Customer Registration" />

      <div className="app-content">
        <form onSubmit={handleFormSubmit} noValidate>
          <div className="app-card">
            <p className="section-title">Personal Information</p>

            <div className="field-group" id="field-name">
              <label className="field-label">Name <span className="field-star">*</span></label>
              <input className="field-input" style={inp('name')} value={form.name} onChange={set('name')} placeholder="Enter full name" />
              <FieldError msg={errors.name} />
            </div>

            <div className="field-group" id="field-email">
              <label className="field-label">Email <span className="field-star">*</span></label>
              <input className="field-input" style={inp('email')} type="email" value={form.email} onChange={set('email')} placeholder="example@email.com" />
              <FieldError msg={errors.email} />
            </div>

            <div className="field-group" id="field-mobileNo">
              <label className="field-label">Mobile Number <span className="field-star">*</span></label>
              <input className="field-input" style={inp('mobileNo')} type="tel" inputMode="numeric" maxLength={10} value={form.mobileNo} onChange={setNum('mobileNo')} placeholder="10-digit mobile number" />
              <FieldError msg={errors.mobileNo} />
            </div>

            <div className="field-group" id="field-dob">
              <label className="field-label">Date of Birth <span className="field-star">*</span></label>
              <input className="field-input" style={inp('dob')} type="date" value={form.dob} onChange={set('dob')} />
              <FieldError msg={errors.dob} />
            </div>

            <div className="field-group" id="field-gender">
              <label className="field-label">Gender <span className="field-star">*</span></label>
              <select className="field-select" style={inp('gender')} value={form.gender} onChange={set('gender')}>
                <option value="" hidden>Select Gender</option>
                <option value="1">Male</option>
                <option value="2">Female</option>
                <option value="3">Other</option>
              </select>
              <FieldError msg={errors.gender} />
            </div>

            <div className="field-group" id="field-maritalStatus">
              <label className="field-label">Marital Status <span className="field-star">*</span></label>
              <select className="field-select" style={inp('maritalStatus')} value={form.maritalStatus} onChange={set('maritalStatus')}>
                <option value="" hidden>Select Status</option>
                <option value="1">Unmarried</option>
                <option value="2">Married</option>
                <option value="3">Divorced</option>
                <option value="4">Widowed</option>
              </select>
              <FieldError msg={errors.maritalStatus} />
            </div>

            {form.maritalStatus === '2' && (
              <div className="field-group" id="field-spouseName">
                <label className="field-label">Spouse Name <span className="field-star">*</span></label>
                <input className="field-input" style={inp('spouseName')} value={form.spouseName} onChange={set('spouseName')} placeholder="Enter spouse's full name" />
                <FieldError msg={errors.spouseName} />
              </div>
            )}

            <div className="field-group" id="field-fatherName">
              <label className="field-label">Father Name <span className="field-star">*</span></label>
              <input className="field-input" style={inp('fatherName')} value={form.fatherName} onChange={set('fatherName')} placeholder="Enter father's full name" />
              <FieldError msg={errors.fatherName} />
            </div>
          </div>

          <div className="app-card">
            <p className="section-title">KYC Details</p>

            <div className="field-group" id="field-panNumber">
              <label className="field-label">PAN Number <span className="field-star">*</span></label>
              <input
                className="field-input" style={{ textTransform: 'uppercase', ...inp('panNumber') }}
                maxLength={10} value={form.panNumber}
                onChange={(e) => { set('panNumber')({ target: { value: e.target.value.toUpperCase() } }); }}
                placeholder="e.g. ABCDE1234F"
              />
              <FieldError msg={errors.panNumber} />
            </div>

            <div className="field-group" id="field-aadhaarNumber">
              <label className="field-label">Aadhaar Number <span className="field-star">*</span></label>
              <input className="field-input" style={inp('aadhaarNumber')} type="tel" inputMode="numeric" maxLength={12} value={form.aadhaarNumber} onChange={setNum('aadhaarNumber')} placeholder="12-digit Aadhaar number" />
              <FieldError msg={errors.aadhaarNumber} />
            </div>

            <div className="field-group" id="field-pinCode">
              <label className="field-label">Pin Code <span className="field-star">*</span></label>
              <input className="field-input" style={inp('pinCode')} type="tel" inputMode="numeric" maxLength={6} value={form.pinCode} onChange={setNum('pinCode')} placeholder="6-digit pin code" />
              <FieldError msg={errors.pinCode} />
            </div>
          </div>

          <div className="app-card">
            <p className="section-title">Loan Details</p>

            <div className="field-group" id="field-loanAmount">
              <label className="field-label">Loan Amount <span className="field-star">*</span></label>
              <input className="field-input" style={inp('loanAmount')} type="tel" inputMode="numeric" value={form.loanAmount} onChange={setNum('loanAmount')} placeholder="Enter loan amount in ₹" />
              <FieldError msg={errors.loanAmount} />
            </div>

            <div className="field-group" id="field-loanType">
              <label className="field-label">Loan Type <span className="field-star">*</span></label>
              <select className="field-select" style={inp('loanType')} value={form.loanType} onChange={set('loanType')}>
                <option value="" hidden>Select Loan Type</option>
                <option value="1">Personal Loan</option>
                <option value="2">Home Loan</option>
                <option value="3">Business Loan</option>
              </select>
              <FieldError msg={errors.loanType} />
            </div>

            <div className="field-group" id="field-loanGroup">
              <label className="field-label">Loan Group <span className="field-star">*</span></label>
              <select className="field-select" style={inp('loanGroup')} value={form.loanGroup} onChange={set('loanGroup')}>
                <option value="" hidden>Select Loan Group</option>
                <option value="1">Individual</option>
                <option value="2">Group</option>
              </select>
              <FieldError msg={errors.loanGroup} />
            </div>
          </div>

          <div className="app-card" id="field-updatesConsent">
            <div className="check-row" style={errors.updatesConsent ? { background: '#fff8f8', borderRadius: 8, padding: '8px 4px' } : {}}>
              <input type="checkbox" checked={form.updatesConsent} onChange={set('updatesConsent')} />
              <label>I agree to receive updates via WhatsApp, SMS, and Email.</label>
            </div>
            <FieldError msg={errors.updatesConsent} />

            <div id="field-termsConsent" className="check-row" style={{ marginTop: 10, ...(errors.termsConsent ? { background: '#fff8f8', borderRadius: 8, padding: '8px 4px' } : {}) }}>
              <input type="checkbox" checked={form.termsConsent} onChange={set('termsConsent')} />
              <label>I accept the Terms and Conditions & Privacy Policy.</label>
            </div>
            <FieldError msg={errors.termsConsent} />
          </div>

          <button type="submit" className="btn-primary-app" disabled={loading}>
            {loading ? 'Processing...' : 'Register Customer'}
          </button>
        </form>
      </div>

      {/* Success Modal */}
      <Modal show={showSuccessModal} onHide={() => navigate(`/Address?applicationId=${applicationId}&customerId=${customerId}`)} centered>
        <Modal.Body style={{ padding: 0, borderRadius: 16, overflow: 'hidden' }}>
          <div style={{ background: 'linear-gradient(135deg, #1d2e77 0%, #11c232 100%)', padding: '32px 24px 24px', textAlign: 'center' }}>
            <div style={{
              width: 72, height: 72, borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px', fontSize: 36, color: '#fff',
            }}>✓</div>
            <p style={{ color: '#fff', fontWeight: 700, fontSize: 20, margin: 0 }}>Registration Successful!</p>
          </div>
          <div style={{ padding: '20px 24px' }}>
            <p style={{ fontWeight: 700, fontSize: 15, color: '#1d2e77', marginBottom: 12 }}>{form.name}</p>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 13, color: '#888' }}>Customer ID</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#333' }}>{customerId}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <span style={{ fontSize: 13, color: '#888' }}>Application ID</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#333' }}>{applicationId}</span>
            </div>
            <button
              onClick={() => navigate(`/Address?applicationId=${applicationId}&customerId=${customerId}`)}
              style={{
                width: '100%', background: '#1d2e77', color: '#fff',
                border: 'none', borderRadius: 12, height: 50,
                fontWeight: 700, fontSize: 15, cursor: 'pointer',
              }}
            >
              Continue to Address Details
            </button>
          </div>
        </Modal.Body>
      </Modal>

      {/* API Error Modal */}
      <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} centered>
        <Modal.Body style={{ padding: 0, borderRadius: 16, overflow: 'hidden' }}>
          <div style={{ background: '#ffeaea', padding: '28px 24px 20px', textAlign: 'center', borderBottom: '1px solid #ffcccc' }}>
            <div style={{
              width: 60, height: 60, borderRadius: '50%',
              background: '#e53935', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 14px', fontSize: 28, fontWeight: 700,
            }}>✕</div>
            <p style={{ fontWeight: 700, fontSize: 17, color: '#c62828', margin: 0 }}>Registration Failed</p>
          </div>
          <div style={{ padding: '16px 24px 24px', textAlign: 'center' }}>
            <p style={{ fontSize: 13, color: '#555', marginBottom: 20, lineHeight: 1.6 }}>{apiError}</p>
            <button
              onClick={() => setShowErrorModal(false)}
              style={{
                width: '100%', background: '#1d2e77', color: '#fff',
                border: 'none', borderRadius: 12, height: 48,
                fontWeight: 700, fontSize: 14, cursor: 'pointer',
              }}
            >
              Try Again
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Register;
